import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";
import { invitationExpiryCopy } from "../lib/invitation-expiry.js";

type Tab = "account" | "users";

export default function SettingsPage(): React.ReactElement {
  // Business costs moved OUT of Settings — they live on the Costs page now, so
  // there's one home for them (founder 2026-07-06: "it's also still in
  // settings"). The BusinessCostsTab component below stays exported for /costs.
  const [tab, setTab] = useState<Tab>("account");

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24 }}>Settings</h1>

      <div style={tabStyles.bar}>
        <TabButton label="Account" active={tab === "account"} onClick={() => setTab("account")} />
        <TabButton label="Users" active={tab === "users"} onClick={() => setTab("users")} />
      </div>

      <div style={{ marginTop: 24 }}>
        {tab === "account" && <AccountTab />}
        {tab === "users" && <UsersTab />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

interface UserRow {
  id: string;
  clerkUserId: string;
  name: string | null;
  email: string | null;
  role: "owner" | "admin" | "member" | "viewer";
  createdAt: string;
}

interface InvitationRow {
  id: string;
  email: string;
  role: "owner" | "admin" | "member" | "viewer";
  status: "pending" | "accepted" | "revoked" | "expired";
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  providerKind?: "clerk_application_invitation" | "clerk_sign_in_token";
}

interface GeneratedInvite {
  url: string;
  expiresAt: string;
  providerKind: InvitationRow["providerKind"];
}

function safeInvitationError(error: unknown): string {
  const message = (error as { detail?: { error?: unknown } } | null)?.detail?.error;
  return typeof message === "string"
    ? message
    : "Couldn't create the invite. Check the email and try again.";
}

function UsersTab(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: ["account", "users"],
    queryFn: () => api.get<{ users: UserRow[] }>("/account/users"),
  });
  const invitations = useQuery({
    queryKey: ["account", "invitations"],
    queryFn: () => api.get<{ invitations: InvitationRow[] }>("/account/invitations"),
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin" | "owner" | "viewer">("member");
  const [lastInvite, setLastInvite] = useState<GeneratedInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const toast = useToast();
  const invite = useMutation({
    mutationFn: () =>
      api.post<{ invitation: InvitationRow; inviteUrl: string }>("/account/invitations", { email, role }),
    onSuccess: (data) => {
      setLastInvite({
        url: data.inviteUrl,
        expiresAt: data.invitation.expiresAt,
        providerKind: data.invitation.providerKind,
      });
      setEmail("");
      void queryClient.invalidateQueries({ queryKey: ["account", "invitations"] });
    },
    onError: (error) => toast.error(safeInvitationError(error)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/account/invitations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["account", "invitations"] });
      toast.success("Invite revoked.");
    },
    onError: () => toast.error("Couldn't revoke the invite."),
  });

  const removeMember = useMutation({
    mutationFn: (id: string) => api.delete(`/account/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["account", "users"] });
      toast.success("Team member removed.");
    },
    onError: () => toast.error("Couldn't remove that team member."),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <section style={sectionStyles.card}>
        <h2 style={sectionStyles.title}>Invite a team member</h2>
        <p style={sectionStyles.description}>
          Send an invite link. New users create a login; existing users sign in. They must use
          the invited email, and they’ll join this account—not create a new one.
        </p>

        <form
          style={sectionStyles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (email.includes("@")) invite.mutate();
          }}
        >
          <input
            type="email"
            placeholder="them@theirdomain.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setLastInvite(null);
            }}
            style={{ ...sectionStyles.input, width: 280 }}
          />
          <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} style={sectionStyles.select}>
            <option value="viewer">Viewer (read-only — can see, can't change anything)</option>
            <option value="member">Member (can use tools, no settings)</option>
            <option value="admin">Admin (can change settings)</option>
            <option value="owner">Owner (full access)</option>
          </select>
          <button type="submit" style={sectionStyles.saveButton} disabled={invite.isPending || !email.includes("@")}>
            {invite.isPending ? "Creating…" : "Generate invite link"}
          </button>
        </form>

        {lastInvite && (
          <div style={inviteLinkStyles.box}>
            <p style={{ margin: 0, fontSize: 13, color: "var(--body)" }}>
              Send this personal link only to your invitee. {invitationExpiryCopy(lastInvite.expiresAt)}
            </p>
            <div style={inviteLinkStyles.row}>
              <code style={inviteLinkStyles.url}>{lastInvite.url}</code>
              <button
                style={inviteLinkStyles.copyButton}
                onClick={async () => {
                  await navigator.clipboard.writeText(lastInvite.url);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </section>

      <section style={sectionStyles.card}>
        <h2 style={sectionStyles.title}>Team members</h2>
        {users.isLoading ? (
          <p style={{ color: "var(--body)" }}>Loading…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
            {(users.data?.users ?? []).map((u) => (
              <div key={u.id} style={memberStyles.row}>
                <div>
                  <div style={{ fontSize: 14 }}>
                    {u.name ?? u.email ?? `${u.clerkUserId.substring(0, 12)}…`}{" "}
                    <span style={memberStyles.roleBadge(u.role)}>{u.role}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>
                    {u.name && u.email ? `${u.email} · ` : ""}Joined {new Date(u.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  style={memberStyles.removeButton}
                  onClick={() => {
                    if (window.confirm("Remove this team member?")) removeMember.mutate(u.id);
                  }}
                  disabled={removeMember.isPending}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={sectionStyles.card}>
        <h2 style={sectionStyles.title}>Pending + past invites</h2>
        {invitations.isLoading ? (
          <p style={{ color: "var(--body)" }}>Loading…</p>
        ) : (invitations.data?.invitations ?? []).length === 0 ? (
          <p style={{ color: "var(--body)", marginTop: 8 }}>No invitations yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
            {(invitations.data?.invitations ?? []).map((inv) => (
              <div key={inv.id} style={memberStyles.row}>
                <div>
                  <div style={{ fontSize: 14 }}>
                    {inv.email}{" "}
                    <span style={memberStyles.roleBadge(inv.role)}>{inv.role}</span>{" "}
                    <span style={memberStyles.statusBadge(inv.status)}>{inv.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>
                    Sent {new Date(inv.createdAt).toLocaleDateString()}
                    {inv.status === "pending" && ` · expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                  </div>
                </div>
                {inv.status === "pending" && (
                  <button style={memberStyles.removeButton} onClick={() => revoke.mutate(inv.id)}>
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const inviteLinkStyles: Record<string, React.CSSProperties> = {
  box: {
    marginTop: 16,
    padding: "12px 16px",
    background: "var(--surface-2)",
    border: "1px solid color-mix(in srgb, var(--good) 30%, var(--border))",
    borderRadius: 8,
  },
  row: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginTop: 8,
  },
  url: {
    flex: 1,
    fontFamily: "ui-monospace, monospace",
    fontSize: 12,
    color: "var(--text)",
    background: "var(--surface-2)",
    padding: "8px 12px",
    borderRadius: 6,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  copyButton: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },
};

const memberStyles = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 14px",
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: 6,
  } as React.CSSProperties,
  roleBadge: (role: string): React.CSSProperties => ({
    display: "inline-block",
    padding: "2px 8px",
    background:
      role === "owner" ? "color-mix(in srgb, var(--good) 20%, transparent)" : role === "admin" ? "color-mix(in srgb, var(--accent) 20%, transparent)" : "var(--surface-2)",
    color: role === "owner" ? "var(--good)" : role === "admin" ? "var(--accent)" : "var(--body)",
    borderRadius: 4,
    fontSize: 11,
    marginLeft: 6,
  }),
  statusBadge: (status: string): React.CSSProperties => ({
    display: "inline-block",
    padding: "2px 8px",
    background:
      status === "pending"
        ? "color-mix(in srgb, var(--c2) 20%, transparent)"
        : status === "accepted"
          ? "color-mix(in srgb, var(--good) 20%, transparent)"
          : "color-mix(in srgb, var(--bad) 20%, transparent)",
    color:
      status === "pending"
        ? "var(--c2)"
        : status === "accepted"
          ? "var(--good)"
          : "var(--bad)",
    borderRadius: 4,
    fontSize: 11,
    marginLeft: 6,
  }),
  removeButton: {
    background: "transparent",
    color: "var(--bad)",
    border: "1px solid color-mix(in srgb, var(--bad) 35%, var(--border))",
    padding: "4px 10px",
    borderRadius: 4,
    fontSize: 12,
    cursor: "pointer",
  } as React.CSSProperties,
};

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      style={{
        ...tabStyles.button,
        ...(active ? tabStyles.buttonActive : {}),
      }}
    >
      {label}
    </button>
  );
}

// ----------------------------------------------------------------------------

interface OverheadResponse {
  overhead: {
    monthlyOverheadAmount: string;
    currency: string;
    source: string;
    effectiveDate: string;
    notes: string | null;
  } | null;
}

interface OverheadHistoryRow {
  monthlyOverheadAmount: string;
  currency: string;
  source: string;
  effectiveDate: string;
  active: boolean;
  notes: string | null;
  createdAt: string;
}

// Exported: the Costs page (/costs) renders these same sections so every cost
// input lives on one page — the numbers save to the same place either way.
export function BusinessCostsTab(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const overhead = useQuery({
    queryKey: ["overhead"],
    queryFn: () => api.get<OverheadResponse>("/business-costs/overhead"),
  });
  const overheadReview = useQuery({
    queryKey: ["overhead-review"],
    queryFn: () => api.get<{ source: { platform: string } | null; summary: { readyToUse: boolean; unreviewedAccountCount: number } }>("/business-costs/overhead/review"),
  });
  const hasAccountingReview = overheadReview.data?.source !== null && overheadReview.data?.source !== undefined;
  // Overhead saves in the ACCOUNT's currency — never silently stamped USD.
  const { data: acct } = useQuery({
    queryKey: ["account"],
    queryFn: () => api.get<AccountResponse>("/account"),
  });
  const accountCurrency = acct?.account.baseCurrency || "USD";

  // Manual-only accounts keep the simple editable total. Once account-level
  // books are available, this becomes a read-only summary with a link to the
  // reconciled review so a manual total cannot silently bypass source detail.
  const [overheadDraft, setOverheadDraft] = useState<string | null>(null);

  // Overhead history: every version is kept (effective-dated, never deleted) —
  // this makes "I changed this in March" auditable, and shows each number's
  // provenance note (e.g. the folded-in gateway fees disclosure). Loaded only
  // when opened.
  const [showOverheadHistory, setShowOverheadHistory] = useState(false);
  const overheadHistory = useQuery({
    queryKey: ["overhead-history"],
    queryFn: () => api.get<{ history: OverheadHistoryRow[] }>("/business-costs/overhead/history"),
    enabled: showOverheadHistory,
  });

  const toast = useToast();
  const saveOverhead = useMutation({
    mutationFn: (amount: number) =>
      api.post("/business-costs/overhead", {
        monthlyOverheadAmount: amount,
        currency: accountCurrency,
        source: "manual_estimate",
      }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["overhead"] });
      void queryClient.invalidateQueries({ queryKey: ["overhead-history"] });
      setOverheadDraft(null);
      toast.success("Monthly overhead saved.");
    },
    onError: () => toast.error("Couldn't save the overhead amount."),
  });

  const currentAmount = overhead.data?.overhead
    ? Number(overhead.data.overhead.monthlyOverheadAmount)
    : null;
  const savedStr = currentAmount !== null ? String(currentAmount) : "";
  const shown = overheadDraft ?? savedStr;
  const overheadDirty = overheadDraft !== null && overheadDraft.trim() !== savedStr;
  const submitOverhead = (): void => {
    const amount = Number(shown);
    if (!Number.isFinite(amount) || amount < 0) return;
    saveOverhead.mutate(amount);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <section style={sectionStyles.card}>
        <h2 style={sectionStyles.title}>Monthly overhead</h2>
        <p style={sectionStyles.description}>
          Your regular business costs that are not tied to one product, such as rent, office payroll,
          software, insurance, and utilities.
        </p>
        {hasAccountingReview ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div className="dd-num" style={{ fontSize: 20, fontWeight: 750 }}>
                {currentAmount === null
                  ? "Not confirmed yet"
                  : `${new Intl.NumberFormat("en-US", { style: "currency", currency: accountCurrency, maximumFractionDigits: 0 }).format(currentAmount)}/mo`}
              </div>
              <div style={{ color: "var(--faint)", fontSize: 11.5, marginTop: 3 }}>
                {overheadReview.data?.summary.readyToUse
                  ? "Your accounting review is ready to confirm or update."
                  : `${overheadReview.data?.summary.unreviewedAccountCount ?? 0} account${overheadReview.data?.summary.unreviewedAccountCount === 1 ? " needs" : "s need"} an answer.`}
              </div>
            </div>
            <Link to="/costs/overhead" style={{ ...sectionStyles.saveButton, display: "inline-flex", textDecoration: "none" }}>
              Review individual expenses
            </Link>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={{ position: "absolute", left: 11, fontSize: 13, color: "var(--faint)" }}>$</span>
              <input
                type="number" inputMode="decimal" placeholder="0.00" min="0" step="0.01"
                value={shown}
                onChange={(e) => setOverheadDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (overheadDirty) submitOverhead(); } }}
                style={{ ...sectionStyles.input, paddingLeft: 22, width: 160 }}
              />
              <span style={{ position: "absolute", right: 11, fontSize: 12, color: "var(--faint)" }}>/mo</span>
            </div>
            {overheadDirty && (
              <button type="button" style={sectionStyles.saveButton} disabled={saveOverhead.isPending} onClick={submitOverhead}>
                {saveOverhead.isPending ? "Saving…" : "Save"}
              </button>
            )}
          </div>
        )}
        {/* Provenance of the CURRENT number — the notes disclose anything folded
            in (gateway fees from setup, parked costs), so the amount you see is
            never silently different from the amount you typed. */}
        {overhead.data?.overhead?.notes && (
          <div style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 8, lineHeight: 1.5 }}>
            {overhead.data.overhead.notes}
          </div>
        )}
        {currentAmount !== null && (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={() => setShowOverheadHistory((v) => !v)} style={adCollapseBtn}>
              <span style={{ transform: showOverheadHistory ? "rotate(90deg)" : "none", transition: "transform .12s" }}>▸</span>
              History
            </button>
            {showOverheadHistory && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                {(overheadHistory.data?.history ?? []).map((h, i) => (
                  <div key={`${h.effectiveDate}-${h.createdAt}-${i}`} style={{ fontSize: 12, color: h.active ? "var(--body)" : "var(--faint)", lineHeight: 1.5 }}>
                    <b className="dd-num">{new Intl.NumberFormat("en-US", { style: "currency", currency: h.currency || "USD", maximumFractionDigits: 0 }).format(Number(h.monthlyOverheadAmount))}/mo</b>
                    {" "}since {overheadDate(h.effectiveDate)}{h.active ? " — current" : ""}
                    {h.notes ? <span> · {h.notes}</span> : null}
                  </div>
                ))}
                {overheadHistory.isLoading && <div style={{ fontSize: 12, color: "var(--faint)" }}>Loading…</div>}
                {overheadHistory.data && overheadHistory.data.history.length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--faint)" }}>No earlier versions.</div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <AdSpendPlanSection accountCurrency={accountCurrency} />
    </div>
  );
}

interface AdSpendEntry {
  year: number;
  month: number;
  channel: string;
  adSpendAmount: number;
  currency: string;
  /** 'manual' = typed here; 'auto_pull' = rolled up from a connected ad
   *  platform's sync. Auto rows are read-only — removing one would just write
   *  a $0 manual row the next rollup overwrites (a silent fight). */
  source?: string;
}

/** Channels you can enter spend for (matches the backend AD_SPEND_CHANNELS).
 *  Grouped: OFF-PLATFORM first (the main use — spend your connections can't
 *  see), platforms below as a fallback for accounts you haven't connected. */
const AD_CHANNEL_GROUPS: Array<{ group: string; options: Array<{ value: string; label: string }> }> = [
  { group: "Off-platform (connections can't see these)", options: [
    { value: "tv", label: "TV" },
    { value: "radio", label: "Radio" },
    { value: "direct_mail", label: "Direct mail" },
    { value: "newsletter", label: "Paid newsletter / sponsorship email" },
    { value: "podcast", label: "Podcast ads" },
    { value: "sponsorship", label: "Sponsorships" },
    { value: "other_offline", label: "Other off-platform" },
  ] },
  { group: "Ad platforms (only if NOT connected)", options: [
    { value: "meta_ads", label: "Meta (Facebook/Instagram)" },
    { value: "google_ads", label: "Google Ads" },
    { value: "tiktok_ads", label: "TikTok Ads" },
    { value: "unattributed", label: "Other / blended (not tied to a channel)" },
  ] },
];
const AD_CHANNEL_OPTIONS = AD_CHANNEL_GROUPS.flatMap((g) => g.options);
const adChannelLabel = (ch: string): string => AD_CHANNEL_OPTIONS.find((o) => o.value === ch)?.label ?? ch;
// Off-platform channels only — the ones a connected ad account NEVER auto-pulls.
// An ongoing commitment is restricted to these so it can't double-count a
// platform's spend once that platform is connected.
const OFFPLATFORM_CHANNELS = new Set((AD_CHANNEL_GROUPS[0]?.options ?? []).map((o) => o.value));

interface RecurringAdSpendRow {
  channel: string;
  monthlyAmount: number;
  currency: string;
  startYear: number;
  startMonth: number;
  endYear?: number;
  endMonth?: number;
}

/** Off-platform ad spend as ONE-TIME vs ONGOING rows (founder 2026-07-06: drop
 *  the month-by-month grid). One-time = a single month's spend; ongoing = a
 *  fixed monthly amount that applies every month from a start (the backend
 *  expands it into MER/CAC for any window, including future months). */
function AdSpendPlanSection({ accountCurrency }: { accountCurrency: string }): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const today = new Date();

  const plan = useQuery({
    queryKey: ["ad-spend-plan"],
    queryFn: () => api.get<{ oneTime: AdSpendEntry[]; recurring: RecurringAdSpendRow[] }>("/business-costs/ad-spend/plan"),
  });

  const [kind, setKind] = useState<"ongoing" | "one_time">("ongoing");
  const [showOngoing, setShowOngoing] = useState(false);
  const [showOneTime, setShowOneTime] = useState(false);
  const [channel, setChannel] = useState<string>("newsletter");
  const [amount, setAmount] = useState<string>("");
  const [month, setMonth] = useState<number>(today.getMonth() + 1);
  const [year, setYear] = useState<number>(today.getFullYear());

  const refresh = (): void => {
    relockFinancialQueries(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["ad-spend-plan"] });
    void queryClient.invalidateQueries({ queryKey: ["ad-spend"] });
    // Spend unlocks / moves the MER + CAC card family.
  };

  const add = useMutation({
    mutationFn: (input: { kind: "ongoing" | "one_time"; channel: string; amount: number; month: number; year: number }) =>
      input.kind === "ongoing"
        ? api.post("/business-costs/ad-spend/recurring", { channel: input.channel, monthlyAmount: input.amount, currency: accountCurrency, startYear: input.year, startMonth: input.month })
        : api.post("/business-costs/ad-spend", { channel: input.channel, adSpendAmount: input.amount, currency: accountCurrency, year: input.year, month: input.month }),
    onSuccess: (_d, input) => {
      refresh();
      setAmount("");
      // Open the section the new row landed in, so the founder sees it appear.
      if (input.kind === "ongoing") setShowOngoing(true); else setShowOneTime(true);
      toast.success(input.kind === "ongoing"
        ? `Ongoing spend saved — ${adChannelLabel(input.channel)}, applied every month. Your MER and CAC cards use it.`
        : `One-time spend saved for ${monthLabel(input.year, input.month)}. Your MER and CAC cards use it.`);
    },
    onError: () => toast.error("Couldn't save that — check the numbers."),
  });

  const removeOngoing = useMutation({
    mutationFn: (r: RecurringAdSpendRow) => api.post("/business-costs/ad-spend/recurring", { channel: r.channel, monthlyAmount: 0, startYear: r.startYear, startMonth: r.startMonth }),
    onSuccess: () => { refresh(); toast.success("Removed."); },
    onError: () => toast.error("Couldn't remove that — try again."),
  });
  const removeOneTime = useMutation({
    mutationFn: (e: AdSpendEntry) => api.post("/business-costs/ad-spend", { channel: e.channel, adSpendAmount: 0, currency: e.currency, year: e.year, month: e.month }),
    onSuccess: () => { refresh(); toast.success("Removed."); },
    onError: () => toast.error("Couldn't remove that — try again."),
  });

  const oneTime = (plan.data?.oneTime ?? []).filter((e) => e.adSpendAmount > 0);
  const recurring = plan.data?.recurring ?? [];
  const money = (v: number, ccy: string): string => new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(v);

  return (
    <section style={sectionStyles.card}>
      <h2 style={sectionStyles.title}>Off-platform ad spend</h2>
      <p style={sectionStyles.description}>
        Advertising your <strong>connected ad accounts can't see</strong> — TV, radio, direct mail,
        paid newsletters, podcasts, sponsorships. Set it up <strong>ongoing</strong> ($ every month)
        or <strong>one-time</strong> (a single month). If Meta, Google, or TikTok are connected,
        their spend flows in automatically — leave those out here. Affiliate commissions come in
        automatically too — don't include them.
      </p>

      {/* Add anything new — always visible */}
      <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
        {(["ongoing", "one_time"] as const).map((k) => (
          <button key={k} type="button" onClick={() => { setKind(k); if (k === "ongoing" && !OFFPLATFORM_CHANNELS.has(channel)) setChannel("newsletter"); }}
            style={{ padding: "7px 16px", fontSize: 12.5, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
              background: kind === k ? "var(--accent)" : "transparent", color: kind === k ? "var(--accent-contrast)" : "var(--body)" }}>
            {k === "ongoing" ? "Ongoing" : "One-time"}
          </button>
        ))}
      </div>

      <form
        style={sectionStyles.form}
        onSubmit={(e) => {
          e.preventDefault();
          const a = Number(amount);
          if (!Number.isFinite(a) || a <= 0) return;
          add.mutate({ kind, channel, amount: a, month, year });
        }}
      >
        <select value={channel} onChange={(e) => setChannel(e.target.value)} style={sectionStyles.select} aria-label="Channel">
          {(kind === "ongoing" ? AD_CHANNEL_GROUPS.slice(0, 1) : AD_CHANNEL_GROUPS).map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </optgroup>
          ))}
        </select>
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <span style={{ position: "absolute", left: 11, fontSize: 13, color: "var(--faint)" }}>$</span>
          <input type="number" placeholder={kind === "ongoing" ? "per month" : "amount"} value={amount}
            onChange={(e) => setAmount(e.target.value)} style={{ ...sectionStyles.input, paddingLeft: 22 }} min="0" step="0.01" />
        </div>
        <span style={{ fontSize: 12.5, color: "var(--faint)", alignSelf: "center" }}>{kind === "ongoing" ? "since" : "in"}</span>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={sectionStyles.select}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{new Date(2025, m - 1).toLocaleString("default", { month: "long" })}</option>
          ))}
        </select>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={sectionStyles.select}>
          {[today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <button type="submit" style={sectionStyles.saveButton} disabled={add.isPending}>
          {add.isPending ? "Saving…" : "Add"}
        </button>
      </form>

      {/* History — collapsed by default */}
      {recurring.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button type="button" onClick={() => setShowOngoing((v) => !v)} style={adCollapseBtn}>
            <span style={{ transform: showOngoing ? "rotate(90deg)" : "none", transition: "transform .12s" }}>▸</span>
            Ongoing costs ({recurring.length})
          </button>
          {showOngoing && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {recurring.map((r) => (
                <div key={`r-${r.channel}`} style={adRow}>
                  <span style={adBadge("ongoing")}>Ongoing</span>
                  <span style={{ flex: 1, fontSize: 13 }}>
                    <strong>{adChannelLabel(r.channel)}</strong> — {money(r.monthlyAmount, r.currency)}/mo,
                    {" "}since {monthLabel(r.startYear, r.startMonth)}{r.endYear && r.endMonth ? ` – ${monthLabel(r.endYear, r.endMonth)}` : ""}
                  </span>
                  <button type="button" style={adRemoveBtn} disabled={removeOngoing.isPending} onClick={() => removeOngoing.mutate(r)}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {oneTime.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setShowOneTime((v) => !v)} style={adCollapseBtn}>
            <span style={{ transform: showOneTime ? "rotate(90deg)" : "none", transition: "transform .12s" }}>▸</span>
            One-time costs ({oneTime.length})
          </button>
          {showOneTime && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {oneTime.map((e) => (
                <div key={`o-${e.year}-${e.month}-${e.channel}`} style={adRow}>
                  <span style={adBadge("one_time")}>{e.source === "auto_pull" ? "Auto" : "One-time"}</span>
                  <span style={{ flex: 1, fontSize: 13 }}>
                    <strong>{adChannelLabel(e.channel)}</strong> — {money(e.adSpendAmount, e.currency)}, {monthLabel(e.year, e.month)}
                  </span>
                  {/* Auto-pulled rollups are read-only: "removing" one writes a $0
                      manual row the next platform sync overwrites — a silent
                      fight. The badge says where the number really comes from. */}
                  {e.source === "auto_pull" ? (
                    <span style={{ fontSize: 11, color: "var(--faint)", whiteSpace: "nowrap" }}>from your {adChannelLabel(e.channel)} connection</span>
                  ) : (
                    <button type="button" style={adRemoveBtn} disabled={removeOneTime.isPending} onClick={() => removeOneTime.mutate(e)}>Remove</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const adRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface-2)" };
const adCollapseBtn: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: "var(--body)", padding: "2px 0" };
const adRemoveBtn: React.CSSProperties = { fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)", cursor: "pointer", fontFamily: "inherit" };
const adBadge = (kind: "ongoing" | "one_time"): React.CSSProperties => ({
  fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap",
  background: kind === "ongoing" ? "var(--accent-soft, rgba(99,102,241,0.12))" : "var(--surface-3, rgba(120,120,120,0.12))",
  color: kind === "ongoing" ? "var(--accent)" : "var(--body)",
});

function monthLabel(year: number, month: number): string {
  return new Date(year, month - 1).toLocaleString("default", { month: "short", year: "numeric" });
}

/** "2026-07-08" → "Jul 8, 2026" (parsed as a plain date, no timezone shift). */
function overheadDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("default", { month: "short", day: "numeric", year: "numeric" });
}

// ----------------------------------------------------------------------------

interface AccountResponse {
  account: {
    name: string;
    baseCurrency: string;
    industry: string | null;
    revenueRange: string | null;
    timeZone: string;
    logoUrl: string | null;
  };
}

/** Safe operator control for replaying the guided screens on the private test
 * account. Business data stays in place; only progress and approval restart. */
function RestartGuidedOnboardingCard(): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const restart = useMutation({
    mutationFn: () => api.post("/onboarding/restart", {}),
    onSuccess: async () => {
      relockFinancialQueries(queryClient);
      await queryClient.invalidateQueries();
      toast.success("Onboarding restarted. All business data is still here.");
      navigate("/welcome?step=1");
    },
    onError: () => toast.error("We couldn't restart onboarding. Nothing was changed. Try again."),
  });
  return (
    <section style={{ ...sectionStyles.card, marginTop: 16, border: "1px solid color-mix(in srgb, var(--accent) 45%, var(--border))" }}>
      <h2 style={sectionStyles.title}>Test onboarding again</h2>
      <p style={{ fontSize: 13, color: "var(--body)", lineHeight: 1.55, margin: "6px 0 12px" }}>
        Start at the Welcome step again. All business data stays exactly as it is. Only your onboarding progress and the approval that unlocks the dashboard restart.
      </p>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{ background: "transparent", border: "1px solid var(--accent)", color: "var(--accent)", borderRadius: "var(--r-sm)", fontSize: 13, fontWeight: 650, padding: "7px 14px", cursor: "pointer" }}>Restart onboarding only</button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 520 }}>
          <div style={{ fontSize: 13, color: "var(--text)" }}><strong>Business data will stay in place.</strong> You will return to the Welcome step and the dashboard will lock until verification is approved again.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" disabled={restart.isPending} onClick={() => restart.mutate()} style={{ background: "var(--accent)", border: "none", color: "var(--accent-contrast)", borderRadius: "var(--r-sm)", fontSize: 13, fontWeight: 650, padding: "7px 14px", cursor: restart.isPending ? "wait" : "pointer", opacity: restart.isPending ? 0.65 : 1 }}>{restart.isPending ? "Restarting…" : "Yes, restart onboarding"}</button>
            <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--body)", borderRadius: "var(--r-sm)", fontSize: 13, padding: "7px 14px", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Destructive test-data reset. It remains separate from the safe onboarding
 * restart and requires the account name so the two actions cannot be confused. */
function EraseTestAccountCard({ accountName }: { accountName: string }): React.ReactElement {
  const api = useApiClient();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const reset = useMutation({
    mutationFn: () => api.post("/account/reset-onboarding", {}),
    onSuccess: async () => {
      relockFinancialQueries(queryClient);
      await queryClient.invalidateQueries();
      toast.success("Test account data erased. The account is ready to rebuild.");
      navigate("/welcome");
    },
    onError: () => toast.error("We couldn't erase the test account. Nothing else was changed."),
  });
  const armed = confirmText.trim() === accountName.trim();
  const dangerBtn: React.CSSProperties = { background: "var(--bad)", border: "none", color: "#fff", borderRadius: "var(--r-sm)", fontSize: 13, fontWeight: 600, padding: "7px 14px", cursor: "pointer" };
  return (
    <section style={{ ...sectionStyles.card, marginTop: 16, border: "1px solid color-mix(in srgb, var(--bad) 45%, var(--border))" }}>
      <h2 style={{ ...sectionStyles.title, color: "var(--bad)" }}>Erase test account data</h2>
      <p style={{ fontSize: 13, color: "var(--body)", lineHeight: 1.55, margin: "6px 0 12px" }}>
        This permanently deletes the test account&apos;s orders, refunds, subscriptions, products, costs, ad spend, overhead, and connections. Use it only when you want to rebuild the entire test account from zero.
      </p>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{ background: "transparent", border: "1px solid var(--bad)", color: "var(--bad)", borderRadius: "var(--r-sm)", fontSize: 13, fontWeight: 600, padding: "7px 14px", cursor: "pointer" }}>Erase all test data</button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 440 }}>
          <label style={{ fontSize: 12.5, color: "var(--body)" }}>Type the account name <strong>{accountName}</strong> to erase all test data:</label>
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={accountName}
            style={{ height: 36, borderRadius: "var(--r-sm)", border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", padding: "0 10px", fontSize: 13 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={!armed || reset.isPending} onClick={() => reset.mutate()} style={{ ...dangerBtn, opacity: armed && !reset.isPending ? 1 : 0.5, cursor: armed && !reset.isPending ? "pointer" : "not-allowed" }}>{reset.isPending ? "Erasing…" : "Yes, erase all test data"}</button>
            <button type="button" onClick={() => { setOpen(false); setConfirmText(""); }} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--body)", borderRadius: "var(--r-sm)", fontSize: 13, padding: "7px 14px", cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function AccountTab(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["account"],
    queryFn: () => api.get<AccountResponse>("/account"),
  });
  // The safe restart + destructive erase controls render only on the PRIVATE
  // test account. Vital Roots powers the public demo and must stay untouched.
  const { data: agencyStatus } = useQuery({
    queryKey: ["agency-status"],
    queryFn: () => api.get<{ isOperator: boolean; resettable: boolean; onboardingRestartable: boolean }>("/account/agency-status"),
  });
  const [editing, setEditing] = useState<{
    name: string;
    industry: string;
    timeZone: string;
  } | null>(null);

  const toast = useToast();
  const save = useMutation({
    mutationFn: (vals: Record<string, string>) => api.patch("/account", vals),
    onSuccess: (_data, vals) => {
      if ("timeZone" in vals || "baseCurrency" in vals) relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      setEditing(null);
      toast.success("Account details saved.");
    },
    onError: () => toast.error("Couldn't save the account details."),
  });

  // Logo upload — small images are stored inline (data URL) in the account's
  // logoUrl, so there's no separate file host to serve them. Cap keeps the row
  // small and the sidebar fast. Shows in the top-left sidebar brand.
  const uploadLogo = (file: File | null): void => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 200 * 1024) { toast.error("Logo must be under 200 KB — try a smaller or compressed image."); return; }
    const reader = new FileReader();
    reader.onload = () => save.mutate({ logoUrl: String(reader.result) });
    reader.onerror = () => toast.error("Couldn't read that image.");
    reader.readAsDataURL(file);
  };

  if (!data) return <p style={{ color: "var(--body)" }}>Loading…</p>;

  if (!editing) {
    return (
      <>
      <section style={sectionStyles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={sectionStyles.title}>Account</h2>
          <button
            style={{ ...sectionStyles.saveButton, fontSize: 13, padding: "6px 14px" }}
            onClick={() =>
              setEditing({
                name: data.account.name,
                industry: data.account.industry ?? "",
                timeZone: data.account.timeZone,
              })
            }
          >
            Edit
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "10px 0 14px", paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>
          <div style={{ width: 56, height: 56, borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
            {data.account.logoUrl ? <img src={data.account.logoUrl} alt="Company logo" style={{ maxWidth: "100%", maxHeight: "100%" }} /> : <span style={{ color: "var(--body-faint)", fontSize: 10.5 }}>No logo</span>}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Company logo</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ ...sectionStyles.saveButton, fontSize: 12.5, padding: "5px 12px", cursor: "pointer", display: "inline-flex" }}>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { uploadLogo(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
                {data.account.logoUrl ? "Replace" : "Upload"}
              </label>
              {data.account.logoUrl && (
                <button onClick={() => save.mutate({ logoUrl: "" })} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--body)", borderRadius: "var(--r-sm)", fontSize: 12.5, padding: "5px 12px", cursor: "pointer" }}>Remove</button>
              )}
            </div>
            <div style={{ fontSize: 11, color: "var(--body-faint)", marginTop: 5 }}>Shows in the top-left sidebar. PNG or SVG, under 200 KB.</div>
          </div>
        </div>
        <Row label="Business name" value={data.account.name} />
        <Row label="Industry" value={data.account.industry ?? "—"} />
        <Row label="Base currency" value={data.account.baseCurrency} />
        <Row label="Time zone" value={data.account.timeZone} />
      </section>
      {agencyStatus?.isOperator && agencyStatus.onboardingRestartable && <RestartGuidedOnboardingCard />}
      {agencyStatus?.isOperator && agencyStatus.onboardingRestartable && <EraseTestAccountCard accountName={data.account.name} />}
      </>
    );
  }

  return (
    <section style={sectionStyles.card}>
      <h2 style={sectionStyles.title}>Edit account</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
        <FieldInput
          label="Business name"
          value={editing.name}
          onChange={(v) => setEditing((e) => (e ? { ...e, name: v } : e))}
        />
        <FieldInput
          label="Industry"
          value={editing.industry}
          onChange={(v) => setEditing((e) => (e ? { ...e, industry: v } : e))}
          placeholder="supplements, apparel, beauty, …"
        />
        <FieldInput
          label="Time zone"
          value={editing.timeZone}
          onChange={(v) => setEditing((e) => (e ? { ...e, timeZone: v } : e))}
          placeholder="America/New_York"
        />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
        <button
          style={sectionStyles.saveButton}
          disabled={save.isPending}
          onClick={() => save.mutate(editing)}
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button
          style={{ ...sectionStyles.saveButton, background: "transparent", color: "var(--body)", border: "1px solid var(--border)" }}
          onClick={() => setEditing(null)}
        >
          Cancel
        </button>
      </div>
    </section>
  );
}

function FieldInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): React.ReactElement {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={rowStyles.label}>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={sectionStyles.input}
      />
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div style={rowStyles.row}>
      <span style={rowStyles.label}>{label}</span>
      <span style={rowStyles.value}>{value}</span>
    </div>
  );
}

// ----------------------------------------------------------------------------

const tabStyles: Record<string, React.CSSProperties> = {
  bar: {
    display: "flex",
    gap: 4,
    marginTop: 16,
    borderBottom: "1px solid var(--border)",
  },
  button: {
    background: "transparent",
    color: "var(--body)",
    border: "none",
    padding: "10px 16px",
    fontSize: 14,
    cursor: "pointer",
    borderBottom: "2px solid transparent",
    marginBottom: -1,
  },
  buttonActive: {
    color: "var(--text)",
    borderBottom: "2px solid var(--accent)",
  },
};

const sectionStyles: Record<string, React.CSSProperties> = {
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 24,
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
  },
  description: {
    color: "var(--body)",
    fontSize: 13,
    marginTop: 6,
    marginBottom: 16,
  },
  currentValue: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    padding: "10px 14px",
    background: "var(--surface-2)",
    borderRadius: 8,
    marginBottom: 16,
  },
  currentLabel: {
    color: "var(--body)",
    fontSize: 12,
  },
  currentAmount: {
    fontSize: 18,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },
  currentMeta: {
    fontSize: 12,
    color: "var(--body)",
    marginLeft: "auto",
  },
  form: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  input: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "8px 10px",
    borderRadius: 6,
    fontSize: 14,
    width: 140,
    fontFamily: "inherit",
  },
  select: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "8px 10px",
    borderRadius: 6,
    fontSize: 14,
    fontFamily: "inherit",
  },
  saveButton: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  entries: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 8,
    marginBottom: 16,
  },
  entry: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 12px",
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    fontSize: 13,
  },
  entryDate: {
    color: "var(--body)",
  },
  entryAmount: {
    fontVariantNumeric: "tabular-nums",
  },
};

const rowStyles: Record<string, React.CSSProperties> = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid var(--border)",
  },
  label: {
    color: "var(--body)",
    fontSize: 13,
  },
  value: {
    fontSize: 14,
  },
};
